import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MsalProvider } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import { useFlags } from "launchdarkly-react-client-sdk";

import { Button as MUIButton, Typography } from "@mui/material";

import { SignInScreenPermissions } from "../SignIn.enum";

import Button from "components/common/Button";

import { DropdownData } from "store/actions/DropdownData";
import { AuthUserSuccess } from "store/actions/AuthUser";
import { UserAdditionalInfo } from "store/actions/UserAdditionalInfo";

import postRequest from "util/APIHelperPost";
import { baseUrl, s3ImagesBaseURL } from "util/APIBaseUrl";

import MicrosoftLogo from "assets/images/microsoft-icon.png";

import "./MicrosoftSignup.scss";
import { ADD_CUSTOMER_INFO_ROUTE } from "constants/routes";
import { resolvePostLoginRoute } from "util/postLoginRedirect";

function MicrosoftAuth(props) {
  const { changeScreen = () => {}, buttonText = "Continue with Microsoft" } = props;
  const { loginRebrand } = useFlags();

  const history = useNavigate();
  const dispatch = useDispatch();
  const [msalInstance, setMsalInstance] = React.useState(null);

  const userAdditionalInfo = useSelector((state) => state.UserAdditionalInfo.userAdditionalInfo);
  const platformConfig = useSelector((state) => state.PlatformConfig.platformConfig);
  const dropdownData = useSelector((state) => state.DropdownData.dropdownData);

  const handleLogin = async () => {
    try {
      // Call the initialization function before using other MSAL API methods
      await msalInstance.handleRedirectPromise();
      const loginResponse = await msalInstance.loginPopup({ scopes: ["User.Read"] });

      const resp = await postRequest(`${baseUrl}/customer/microsoft/auth`, JSON.stringify(loginResponse), true, "POST");

      dispatch(AuthUserSuccess(resp));
    } catch (error) {
      console.error("Login error:", error);
    }
  };

  React.useEffect(() => {
    if (platformConfig?.ok && platformConfig?.payload?.MICROSOFT_CLIENT_ID) {
      const msalConfig = {
        auth: {
          clientId: platformConfig?.payload?.MICROSOFT_CLIENT_ID,
          authority: platformConfig.payload.MICROSOFT_AUTHORITY,
          redirectUri: window.location.origin,
        },
      };
      const instance = new PublicClientApplication(msalConfig);
      setMsalInstance(instance);
    }
  }, [platformConfig.payload.MICROSOFT_CLIENT_ID]);

  React.useEffect(() => {
    const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
    const accessToken = localStorage.getItem("accessToken");

    if (accessToken && loggedInUser) {
      dispatch(DropdownData());
    }
  }, []);

  React.useEffect(() => {
    let loggedInUser = {};
    const accessToken = localStorage.getItem("accessToken");

    if (dropdownData?.ok) {
      if (accessToken !== null && accessToken !== "undefined") {
        loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
        if (loggedInUser !== null && loggedInUser !== undefined) {
          // Only the dashboard destinations below consume the saved route; the setupCompany and
          // addPaymentMethod branches are detours to a screen swap, not a destination, so they
          // deliberately leave it stored.
          if (loggedInUser.type === "customer") {
            if (loggedInUser.setupCompany) {
              if (loginRebrand) {
                history(ADD_CUSTOMER_INFO_ROUTE);
              } else {
                changeScreen(SignInScreenPermissions.ADD_CUSTOMER_INFO);
              }
            } else if (!loggedInUser.bankAttached && userAdditionalInfo.status === "enabled") {
              changeScreen(SignInScreenPermissions.ADD_PAYMENT_METHOD);
            } else {
              history(resolvePostLoginRoute("/main/dashboard"));
            }
          } else if (loggedInUser.type === "employee") {
            dispatch(UserAdditionalInfo(`employee/${loggedInUser.userId}`));
            history(resolvePostLoginRoute("/main/dashboard"));
          } else {
            history(resolvePostLoginRoute("/main/dashboard"));
          }
        }
      }
    }
  }, [dropdownData, userAdditionalInfo]);

  return (
    <div>
      {msalInstance && (
        <MsalProvider instance={msalInstance}>
          {loginRebrand ? (
            <div className="microsoft-signup-button-container">
              <Button
                variant="outlined"
                size="large"
                isFullWidth
                startIcon={
                  <img
                    src={`${s3ImagesBaseURL}/microsoft-btn-icon.svg`}
                    alt="Microsoft btn"
                    className="mr-05"
                    width="26px"
                  />
                }
                label={buttonText}
                onClick={handleLogin}
                className="microsoft-signup-button"
              />
            </div>
          ) : (
            <MUIButton
              type="submit"
              variant="contained"
              fullWidth
              onClick={handleLogin}
              sx={{
                backgroundColor: "#FFFFFF",
                height: "54px",
                fontFamily: "Poppins-Regular !important",
                fontWeight: "500",
                lineHeight: "20",
                borderRadius: "99px",
                fontSize: "16px",
                color: "var(--color-primitives-primary-600)",
                boxShadow: "2px 5px 7px rgba(2, 0, 110, 0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "12px",
                ":hover": {
                  bgcolor: "primary.main",
                  color: "white",
                },
              }}
            >
              <div className="w-3">
                <img src={MicrosoftLogo} alt="microsoft icon" width="26px" />
              </div>
              <Typography>Continue with Microsoft</Typography>
            </MUIButton>
          )}
        </MsalProvider>
      )}
    </div>
  );
}

export default MicrosoftAuth;
